import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-50px'
  },
  transition: {
    duration: 0.6
  }
};
function DiaryDate({
  date
}: {
  date: string;
}) {
  return <div className="flex items-center gap-2 mb-4" data-id="element-90">
      <CalendarIcon className="w-4 h-4" style={{
      color: 'var(--color-brown)'
    }} data-id="element-91" />
      <span className="font-handwritten text-xl" style={{
      color: 'var(--color-brown)'
    }} data-id="element-92">
        {date}
      </span>
    </div>;
}
export function StorySection() {
  return <section className="w-full" data-id="element-93">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-12" data-id="element-94">
        {/* Before */}
        <motion.div {...fadeInUp} className="rounded-xl p-6 md:p-8" style={{
        backgroundColor: 'var(--color-cream-dark)'
      }} data-id="element-95">
          <DiaryDate date="14. März 2024" data-id="element-96" />
          <div className="space-y-4 text-base md:text-lg leading-relaxed" style={{
          color: 'var(--color-text)'
        }} data-id="element-97">
            <p data-id="element-98">
              Ich wusste einfach nicht mehr weiter. Oscar hatte seit Wochen nach
              fast jeder Mahlzeit dieses{' '}
              <strong data-id="element-99">schreckliche Würgen und lautes Magengrummeln</strong>. Er
              schaute mich mit diesen großen Augen an, als würde er sich
              entschuldigen wollen. Dabei konnte er doch gar nichts dafür.
            </p>
            <p data-id="element-100">
              Wir waren schon bei drei verschiedenen Tierärzten. Ich habe
              bestimmt zehn verschiedene Futtersorten ausprobiert. Fischhaltiges
              Futter, getreidefrei, mit Reis, ohne Reis... Nichts hat wirklich
              geholfen. Jedes Mal dachte ich: <em data-id="element-101">Jetzt wird es besser.</em> Und
              dann ging es wieder von vorne los.
            </p>
            <p data-id="element-102">
              Das Schlimmste war, zu sehen, wie er leidet.{' '}
              <strong data-id="element-103">
                Er fraß kaum noch, hatte abgenommen und spielte nicht mehr so
                wie früher.
              </strong>{' '}
              Mein fröhlicher, wilder Wirbelwind war nur noch ein Schatten
              seiner selbst.
            </p>
          </div>
        </motion.div>

        {/* Discovery */}
        <motion.div {...fadeInUp} className="text-center py-6" data-id="element-104">
          <div className="inline-block px-6 py-3 rounded-full" style={{
          backgroundColor: 'var(--color-green-light)'
        }} data-id="element-105">
            <p className="font-handwritten text-2xl md:text-3xl" style={{
            color: 'var(--color-green)'
          }} data-id="element-106">
              ✦ Der Wendepunkt ✦
            </p>
          </div>
        </motion.div>

        {/* Pull Quote */}
        <motion.div {...fadeInUp} className="text-center py-4 px-6 relative" data-id="element-107">
          <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-6xl font-serif opacity-20" style={{
          color: 'var(--color-brown)'
        }} data-id="element-108">
            "
          </span>
          <p className="text-xl md:text-2xl italic relative z-10" style={{
          color: 'var(--color-text)'
        }} data-id="element-109">
            Ich wollte einfach nur, dass es ihm wieder gut geht — das war alles,
            was zählte.
          </p>
        </motion.div>

        <motion.div {...fadeInUp} className="rounded-xl p-6 md:p-8 border-l-4" style={{
        backgroundColor: 'white',
        borderColor: 'var(--color-green)'
      }} data-id="element-110">
          <DiaryDate date="2. April 2024" data-id="element-111" />
          <div className="space-y-4 text-base md:text-lg leading-relaxed" style={{
          color: 'var(--color-text)'
        }} data-id="element-112">
            <p data-id="element-113">
              Eine Freundin aus der Hundegruppe hat mir letzte Woche von einem
              natürlichen Futtertopping erzählt. Ihr Labrador hatte ähnliche
              Probleme, und seit sie das{' '}
              <strong data-id="element-114">Topping übers Futter streut</strong>, ist er wie
              ausgewechselt.
            </p>
            <p data-id="element-115">
              Ich war erst skeptisch – wie oft hatte ich schon etwas
              „Wunderbares" ausprobiert? Ich hatte schon so viele Sachen
              ausprobiert — aber dann sah ich, dass fast 900 andere Hundehalter
              das Topping bewertet hatten, viele mit genau denselben Problemen
              wie Oscar. Aber dann habe ich mir die Inhaltsstoffe angeschaut:{' '}
              <strong data-id="element-116">
                Präbiotika, natürliche Kräuter, keine künstlichen Zusätze
              </strong>
              . Das klang anders als alles, was ich bisher versucht hatte. Kein
              leeres Versprechen, sondern durchdachte Zutaten.
            </p>
            <p className="italic" style={{
            color: 'var(--color-text-light)'
          }} data-id="element-117">
              Ich dachte mir: Was habe ich noch zu verlieren? Also habe ich es
              bestellt.
            </p>
          </div>
        </motion.div>

        {/* After */}
        <motion.div {...fadeInUp} className="rounded-xl p-6 md:p-8" style={{
        backgroundColor: 'var(--color-green-light)'
      }} data-id="element-118">
          <DiaryDate date="28. April 2024" data-id="element-119" />
          <div className="space-y-4 text-base md:text-lg leading-relaxed" style={{
          color: 'var(--color-text)'
        }} data-id="element-120">
            <p data-id="element-121">
              Ich kann es kaum glauben, aber ich muss es aufschreiben:{' '}
              <strong data-id="element-122">Sein Bauch hat sich komplett beruhigt.</strong>
            </p>
            <p data-id="element-123">
              Schon nach der ersten Woche wurde das Magengrummeln leiser. Nach
              zwei Wochen – kein Würgen mehr nach dem Fressen.{' '}
              <strong data-id="element-124">
                Nach 10 Tagen hatte Oscar zum ersten Mal wieder normalen Stuhl.
              </strong>{' '}
              Und jetzt, nach fast vier Wochen? Oscar frisst wieder mit
              Begeisterung, er tobt durch den Garten und hat sogar wieder
              zugenommen.
            </p>
            <p data-id="element-125">
              Ich streue einfach morgens einen Löffel über sein Futter – das
              war's. Keine komplizierten Diätpläne, keine teuren Spezialfutter.
              Einfach dieses eine Topping.
            </p>
            <p className="font-semibold text-lg md:text-xl" style={{
            color: 'var(--color-green-dark)'
          }} data-id="element-126">
              Ich habe meinen Oscar zurück. ♥
            </p>
          </div>
        </motion.div>

        <motion.div {...fadeInUp} className="flex justify-center pt-4" data-id="element-127">
          <CTAButton label="Das Topping das Oscar geholfen hat → hier testen" position="story_cta" data-id="element-128" />
        </motion.div>
      </div>
    </section>;
}